import { duration, utc } from 'moment';
import { precisionRound } from '@peloton/numbers';
import { nbsp } from '@peloton/text';
import { Locale } from '../internationalize/models';
import { toNowTime } from './time';
export var Format = {
  DayFirstSlashDate: 'D/M/YY',
  DayFirstDotDate: 'D.M.YY',
  MonthFirstSlashDate: 'M/D/YY',
  DayFirstSlashDateLongYear: 'D/M/YYYY',
  DayFirstDotDateLongYear: 'D.M.YYYY',
  MonthFirstSlashDateLongYear: 'M/D/YYYY',
  TwoDigitMonthFirstSlashDate: 'MM/DD/YY',
  DayFirstMonthNameShortDate: 'DD MMM YYYY',
  DayFirstMonthNameShortDotDate: 'DD. MMM YYYY',
  MonthNameShortDate: 'MMM DD, YYYY',
  MonthNameMinimalDate: 'MMM D, YYYY',
  MonthNameMinimalDateWithoutYear: 'MMM D',
  Time12Hour: "h:mm".concat(nbsp, "A"),
  Time12HourShort: "h".concat(nbsp, "A"),
  Time24Hour: 'HH:mm',
  Time24HourShort: 'HH',
  DayNameShort: 'ddd',
  DayNameLong: 'dddd',
  DayOfMonth: 'D',
  MonthName: 'MMMM',
  MonthNameShort: 'MMM',
  MonthNumberWithLeadingZero: 'MM',
  MonthNumberWithoutLeadingZero: 'M',
  TwoDigitYear: 'YY',
  FourDigitYear: 'YYYY'
};
export var formatDate = function formatDate(date, formatString) {
  return date.format(formatString);
};
export var formatTimestamp = function formatTimestamp(date) {
  return date.format('ddd M/D/YY @ h:mm A');
};
export var maybeDay = function maybeDay(date) {
  return date.isSame(toNowTime(), 'day') ? '' : date.format('ddd ');
};
export var enforceTwoDigits = function enforceTwoDigits(num) {
  return num.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false
  });
}; // It turns out that moment does not have a solution for formatting durations that works everywhere.

export var formatDuration = function formatDuration(totalSeconds) {
  if (totalSeconds < 0) {
    totalSeconds = 0;
  } else {
    totalSeconds = Math.round(totalSeconds);
  }

  var hours = Math.floor(totalSeconds / 3600);
  var minutes = hours > 0 ? enforceTwoDigits(Math.floor(totalSeconds / 60) % 60) : Math.floor(totalSeconds / 60) % 60;
  var seconds = enforceTwoDigits(totalSeconds % 60); // This will enforce 2 digits of seconds

  return "".concat(hours > 0 ? "".concat(hours, ":") : '').concat(minutes, ":").concat(seconds);
};
export var formatDurationHourAndMinutes = function formatDurationHourAndMinutes(totalSeconds) {
  if (totalSeconds < 0) {
    totalSeconds = 0;
  } else {
    totalSeconds = Math.round(totalSeconds);
  }

  var hours = Math.floor(totalSeconds / 3600);
  var minutes = hours > 0 ? enforceTwoDigits(Math.floor(totalSeconds / 60) % 60) : Math.floor(totalSeconds / 60) % 60;
  return "".concat(hours, ":").concat(minutes);
};
export var formatDurationMinutesAndSeconds = function formatDurationMinutesAndSeconds(totalTime, unit) {
  if (unit === 'min') {
    totalTime = totalTime * 60;
  }

  if (totalTime < 0) {
    totalTime = 0;
  } else {
    totalTime = Math.round(totalTime);
  }

  var totalHours = Math.floor(totalTime / 3600);
  var totalMinutes = totalHours > 0 || totalTime < 600 ? enforceTwoDigits(Math.floor(totalTime / 60) % 60) : Math.floor(totalTime / 60) % 60;
  var totalSeconds = enforceTwoDigits(totalTime % 60);
  return "".concat(totalHours > 0 ? "".concat(totalHours, ":") : '').concat(totalMinutes, ":").concat(totalSeconds);
};
export var formatDurationFromMinutes = function formatDurationFromMinutes(minutes) {
  return formatDuration(minutes * 60);
};
export var formatDurationMinutes = function formatDurationMinutes(seconds) {
  return duration(seconds, 'seconds').asMinutes();
};
export var formatDurationMinutesRound = function formatDurationMinutesRound(seconds) {
  return Math.max(1, Math.round(seconds / 60));
};
export var formatDurationBetween = function formatDurationBetween(startTime, endTime, unit) {
  return precisionRound(duration(endTime.diff(startTime)).as(unit));
};
export var formatDurationByUnit = function formatDurationByUnit(dur, unit) {
  return dur.get(unit);
};
export var formatDateTime = function formatDateTime(date) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    trimLeadingZero: false
  };
  var format = options.trimLeadingZero ? 'h:mm a' : 'hh:mm a';
  return date.format(format);
};
export var formatTimestampDashes = function formatTimestampDashes(date) {
  return date.format('YYYY-MM-DD h:mm A');
};
export var formatDateDashes = function formatDateDashes(date) {
  return date.format('YYYY-MM-DD');
};
/** Yahoo wants duration formatted as HH:MM, i.e. 20 minutes = 00:20 */

export var toHHMMDuration = function toHHMMDuration(durationInSeconds) {
  var d = duration(durationInSeconds, 'seconds');
  return utc(d.as('milliseconds')).format('HH:mm');
};
export var toMMSSDuration = function toMMSSDuration(durationInSeconds) {
  var d = duration(durationInSeconds, 'seconds');
  return utc(d.as('milliseconds')).format('m:ss');
};
export var toIsoString = function toIsoString(date) {
  return date.toISOString();
};
export var toTimerTime = function toTimerTime(durOrSec) {
  var displayHoursThreshold = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 100;
  var dur = toDuration(durOrSec);
  var seconds = formatDurationByUnit(dur, 'seconds');
  var totalMinutesDuration = Math.round(dur.clone().subtract(seconds, 'seconds').asMinutes());

  if (totalMinutesDuration >= displayHoursThreshold) {
    var minutes = Math.floor(formatDurationByUnit(dur, 'minutes'));
    var hours = Math.floor(formatDurationByUnit(dur, 'hours'));
    return "".concat(hours, ":").concat(toLeadingZero(minutes), ":").concat(toLeadingZero(seconds));
  }

  return "".concat(toLeadingZero(totalMinutesDuration), ":").concat(toLeadingZero(seconds));
};
export var toAbsoluteTimerTime = function toAbsoluteTimerTime(durOrSec) {
  var displayHoursThreshold = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 100;
  var leadingZeroMinutes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  var dur = toDuration(durOrSec);
  var seconds = formatDurationByUnit(dur, 'seconds');
  var totalMinutesDuration = Math.round(dur.clone().subtract(seconds, 'seconds').asMinutes());

  if (totalMinutesDuration >= displayHoursThreshold) {
    var minutes = Math.floor(formatDurationByUnit(dur, 'minutes'));
    var hours = Math.floor(formatDurationByUnit(dur, 'hours'));
    return "".concat(Math.abs(hours), ":").concat(toLeadingZero(Math.abs(minutes)), ":").concat(toLeadingZero(Math.abs(seconds)));
  }

  return "".concat(leadingZeroMinutes ? toLeadingZero(Math.abs(totalMinutesDuration)) : Math.abs(totalMinutesDuration), ":").concat(toLeadingZero(Math.abs(seconds)));
};
export var toDuration = function toDuration(durOrSec) {
  return typeof durOrSec === 'number' ? duration(durOrSec, 'seconds') : durOrSec;
};
export var toMonth = function toMonth(date) {
  return date.clone().format('MM');
};
export var toYear = function toYear(date) {
  return date.clone().format('YYYY');
};
export var toDay = function toDay(date) {
  return date.clone().format('DD');
};

var toLeadingZero = function toLeadingZero(s) {
  return s.toString().padStart(2, '0');
};

export var formatTimeForLocale = function formatTimeForLocale(date, locale) {
  var short = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  if (locale === Locale.GermanGermany) {
    return date.format(short ? Format.Time24HourShort : Format.Time24Hour);
  }

  return date.format(short ? Format.Time12HourShort : Format.Time12Hour);
};